'use client';

/**
 * Paid-visitor detection for client-side CTA personalization.
 *
 * Reads the `wisprs_attr` first-touch cookie (httpOnly:false by design — see
 * signup-source-config.ts) AND the current URL, and reports whether this
 * visitor arrived from a paid click. Both sources matter:
 *
 *  - the cookie covers the "clicked an ad yesterday, came back today" visit;
 *  - the URL covers the first paint of the ad click itself, and the write-once
 *    cookie case where an earlier organic touch already claimed the cookie so
 *    a later ad click never reaches it.
 *
 * SEO/ISR constraint (hard): the ad landing pages are STATIC (revalidate=3600).
 * This hook exists so per-visitor variation happens client-side after
 * hydration — the SSR default render IS the crawler render. Never lift this
 * signal into `cookies()` on the server in that tree; it would force the whole
 * page family dynamic. Same body, same claims for every visitor —
 * personalization by arrival, not cloaking (seo-bible Ch. 2.8).
 *
 * Returns `{ isPaid: false }` until mounted, so SSR and first client render
 * agree and there is no hydration mismatch.
 */

import { useEffect, useState } from 'react';
import { detectPaidAttribution as detectEnginePaidAttribution } from '@engine/attribution';

import { SIGNUP_ATTR_COOKIE } from '@/lib/analytics/signup-source-config';

export interface PaidAttribution {
  /** True once mounted AND the visitor is paid-attributed. */
  isPaid: boolean;
  /** utm_campaign when known (e.g. 'switchers-t1'), for labels/analytics. */
  campaign?: string;
}

/** Pure part, exported for tests: cookie + current query string → verdict. */
export const detectPaidAttribution = (rawCookieValue: string | null, search: string): PaidAttribution =>
  detectEnginePaidAttribution(rawCookieValue, search);

function readAttrCookie(): string | null {
  const prefix = `${SIGNUP_ATTR_COOKIE}=`;

  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) return part.slice(prefix.length);
  }

  return null;
}

export function usePaidAttribution(): PaidAttribution {
  const [state, setState] = useState<PaidAttribution>({ isPaid: false });

  useEffect(() => {
    try {
      const next = detectPaidAttribution(readAttrCookie(), window.location.search);

      if (next.isPaid) setState(next);
    } catch {
      // Fail closed: any parse/storage error leaves the default (free-first) CTAs.
    }
  }, []);

  return state;
}
